/**
 * Photo byte integrity (1.60.51 — H3/H4).
 *
 * Additive integrity metadata around the EXISTING photo pipeline. Nothing here
 * captures, compresses, persists, queues, uploads, retries or classifies a
 * photo; it only answers "are these the same bytes?" and reports when they are
 * not.
 *
 * Two hard rules:
 *  1. Hashing is never a prerequisite. When Web Crypto is unavailable (older
 *     WebViews, insecure contexts, test runtimes) `sha256Hex()` returns null
 *     and every caller carries on exactly as it did before 1.60.51.
 *  2. A mismatch is RECOVERABLE. It prevents a photo being called uploaded; it
 *     never deletes bytes, never archives, and never marks a photo terminal.
 */

/**
 * SHA-256 of the exact byte payload, lowercase hex.
 *
 * Returns null — never throws — when the runtime cannot hash. A null hash means
 * "not verified here", never "corrupt".
 */
export async function sha256Hex(bytes: Uint8Array | ArrayBuffer): Promise<string | null> {
  try {
    const subtle = (globalThis.crypto as Crypto | undefined)?.subtle;
    if (!subtle?.digest) return null;
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // Copy into a standalone buffer: the durable record's ArrayBuffer must not
    // be detached or shared with the digest call.
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    const digest = await subtle.digest("SHA-256", copy);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export interface IntegrityExpectation {
  /** Length recorded when the durable payload was persisted, when known. */
  expectedByteLength?: number | null;
  /** SHA-256 recorded for the durable payload, when known. */
  sha256?: string | null;
}

export type IntegrityFailure = {
  ok: false;
  kind: "integrity_length_mismatch" | "integrity_hash_mismatch";
  expected: string | number;
  actual: string | number;
};

export type IntegrityVerdict =
  | { ok: true; reason: "match" | "no_expectation" }
  | IntegrityFailure;

/** Narrow a verdict to its failure, or null when it passed. */
export function integrityFailure(verdict: IntegrityVerdict): IntegrityFailure | null {
  return verdict.ok ? null : (verdict as IntegrityFailure);
}

/**
 * Compare an actual payload against what the durable record expects.
 *
 * Missing expectations are NOT failures: records captured before 1.60.51 (and
 * every legacy v1 record) have no `sha256`/`expectedByteLength` and must keep
 * working untouched.
 */
export function verifyPayloadIntegrity(
  expectation: IntegrityExpectation,
  actual: { byteLength: number; sha256?: string | null },
): IntegrityVerdict {
  const expectedLength = expectation.expectedByteLength;
  if (typeof expectedLength === "number" && expectedLength > 0 && actual.byteLength !== expectedLength) {
    return { ok: false, kind: "integrity_length_mismatch", expected: expectedLength, actual: actual.byteLength };
  }
  const expectedHash = expectation.sha256;
  if (expectedHash && actual.sha256 && expectedHash !== actual.sha256) {
    return { ok: false, kind: "integrity_hash_mismatch", expected: expectedHash, actual: actual.sha256 };
  }
  if (typeof expectedLength === "number" || (expectedHash && actual.sha256)) {
    return { ok: true, reason: "match" };
  }
  return { ok: true, reason: "no_expectation" };
}

/**
 * Storage-side length check.
 *
 * Only meaningful when Storage reports a usable size AND the record carries an
 * expected length. Anything else is "not verified here", never a failure.
 */
export function verifyStorageLength(
  expectation: IntegrityExpectation,
  storageByteLength: number | null | undefined,
): IntegrityVerdict {
  const expectedLength = expectation.expectedByteLength;
  if (typeof expectedLength !== "number" || expectedLength <= 0) return { ok: true, reason: "no_expectation" };
  if (typeof storageByteLength !== "number" || storageByteLength <= 0) return { ok: true, reason: "no_expectation" };
  if (storageByteLength !== expectedLength) {
    return { ok: false, kind: "integrity_length_mismatch", expected: expectedLength, actual: storageByteLength };
  }
  return { ok: true, reason: "match" };
}

/**
 * Bounded SHA-256 (1.60.51 hotfix).
 *
 * `crypto.subtle.digest()` is an off-thread operation with no cancellation and
 * no guaranteed settle time. Hashing sits on the upload path, so it is bounded:
 * if the digest has not resolved within `timeoutMs` the result is null —
 * "integrity verification not completed" — and the caller continues down the
 * existing recoverable upload path. Nothing is failed, archived or deleted.
 */
export async function sha256HexBounded(
  bytes: Uint8Array | ArrayBuffer,
  timeoutMs = 5000,
): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      sha256Hex(bytes),
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
